import React, { FC, useEffect, useReducer } from 'react';
import cx from 'classnames';
import { Asset, InfoFilledIcon } from '@clearcover/atlas';

import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { SelectOption } from 'QuoteApp/services/endpoints/GetConfig';
import { env } from 'Shared/constants/env';

import {
  initialPriceModel,
  PriceContext,
  PriceState,
  transition,
} from '../domain';
import { PriceEvents } from '../domain/constants';
import { WeekdaySelector } from './WeekdaySelector';
import { SemiMonthlySelector } from './SemiMonthlySelector';
import { FrequencySelector } from './FrequencySelector';
import { DayOfMonthSelector } from './DayOfMonthSelector';
import { BiweeklyStartSelector } from './BiweeklyStartSelector';
import styles from './styles.module.css';

const DAYS_NOT_IN_ALL_MONTHS = [29, 30, 31];

type OnSelectorChange = (change: { [key: string]: unknown }) => void;

type SelectorContainerType = {
  isLoading?: boolean;
  initialState?: {
    status: PriceState;
    context: Partial<PriceContext>;
  };
  selectorsData: {
    frequency: Array<SelectOption<string>>;
    weekday: Array<SelectOption<string>>;
    biweeklyDate: Array<SelectOption<string>>;
    semiMonthly: Array<SelectOption<string>>;
    monthly: Array<SelectOption<string>>;
  };
  onSelectorChange: OnSelectorChange;
  onFrequencyChange?: (frequency: FlexiblePaymentFrequency) => void;
};

export const SelectorContainer: FC<SelectorContainerType> = ({
  isLoading,
  initialState,
  selectorsData,
  onSelectorChange,
  onFrequencyChange,
}) => {
  const [priceModel, dispatch] = useReducer(transition, initialPriceModel);
  const { status, context } = priceModel;

  useEffect(() => {
    if (!initialState) return;

    dispatch({
      type: PriceEvents.JUMP_TO_STATE,
      value: initialState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialState?.status,
    initialState?.context.biweeklyDate,
    initialState?.context.dayOfMonth,
    initialState?.context.dayOfWeek,
    initialState?.context.downpayment,
    initialState?.context.frequency,
    initialState?.context.semiMonthlyDates,
  ]);

  const renderFrequencySelector = (className?: string) => (
    <div
      className={cx(styles.selector, styles.selectorHalf, className, styles.z1)}
    >
      <FrequencySelector
        isLoading={isLoading}
        options={selectorsData.frequency}
        onChange={frequency => {
          if (onFrequencyChange) {
            onFrequencyChange(frequency);
          }
          onSelectorChange({ frequency });
          dispatch({ type: PriceEvents.SELECT_FREQUENCY, value: frequency });
        }}
      />
    </div>
  );

  const renderInfoBanner = () => {
    return (
      <div className={styles.infoBanner} data-testid="payment-info-banner">
        <Asset
          icon={InfoFilledIcon}
          description="Payment Info"
          height={16}
          width={16}
        />
        <p>
          Don't worry if you pick a date like the 31st - we'll process your
          payment on the last available day of the month.
        </p>
      </div>
    );
  };

  if (!context.frequency || status === 'idle') {
    return renderFrequencySelector();
  }

  if (context.frequency === 'weekly') {
    const dayOfWeekStatuses: PriceState[] = ['selectingDayOfWeek', 'ready'];

    return (
      <div className={styles.selectorContainer}>
        {renderFrequencySelector()}

        {dayOfWeekStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.selectorHalf, styles.z1)}>
            <WeekdaySelector
              name="dayOfWeek"
              isLoading={isLoading}
              options={selectorsData.weekday}
              onChange={dayOfWeek => {
                onSelectorChange({ dayOfWeek });
                dispatch({
                  type: PriceEvents.SELECT_DAY_OF_WEEK,
                  value: dayOfWeek,
                });
              }}
            />
          </div>
        )}
      </div>
    );
  }

  if (context.frequency === 'biWeekly') {
    const dayOfWeekStatuses: PriceState[] = [
      'selectingDayOfWeek',
      'selectingBiweeklyDate',
      'ready',
    ];
    const biweeklyStatuses: PriceState[] = ['selectingBiweeklyDate', 'ready'];

    return (
      <div className={styles.selectorContainer}>
        {renderFrequencySelector()}

        {dayOfWeekStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.selectorHalf, styles.z1)}>
            <WeekdaySelector
              name="dayOfWeek"
              isLoading={isLoading}
              options={selectorsData.weekday}
              onChange={dayOfWeek => {
                onSelectorChange({ dayOfWeek });
                dispatch({
                  type: PriceEvents.SELECT_DAY_OF_WEEK,
                  value: dayOfWeek,
                });
              }}
            />
          </div>
        )}

        {biweeklyStatuses.includes(status) &&
          env.paymentFlexibilityEveryTwoWeeks && (
            <div
              className={cx(
                styles.selector,
                styles.selectorHalf,
                styles.selectorFull
              )}
            >
              <BiweeklyStartSelector
                options={selectorsData.biweeklyDate}
                onChange={biweeklyDate => {
                  onSelectorChange({ biweeklyDate });
                  dispatch({
                    type: PriceEvents.SELECT_BIWEEKLY_START_DATE,
                    value: biweeklyDate,
                  });
                }}
              />
            </div>
          )}
      </div>
    );
  }

  if (context.frequency === 'semiMonthly') {
    const semiMonthlyStatuses: PriceState[] = [
      'selectingSemiMonthlyDates',
      'ready',
    ];

    return (
      <div className={styles.selectorContainer}>
        {renderFrequencySelector()}

        {semiMonthlyStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.selectorHalf, styles.z1)}>
            <SemiMonthlySelector
              isLoading={isLoading}
              options={selectorsData.semiMonthly}
              onChange={semiMonthlyDates => {
                onSelectorChange({ semiMonthlyDates });
                dispatch({
                  type: PriceEvents.SELECT_SEMI_MONTHLY_DATES,
                  value: semiMonthlyDates,
                });
              }}
            />
          </div>
        )}
      </div>
    );
  }

  if (context.frequency === 'monthly') {
    const monthlyStatuses: PriceState[] = ['selectingDayOfMonth', 'ready'];
    const { dayOfMonth } = context;
    const isIrregularMonthDay =
      dayOfMonth && DAYS_NOT_IN_ALL_MONTHS.includes(parseInt(dayOfMonth, 10));

    return (
      <div className={styles.selectorContainer}>
        {renderFrequencySelector()}

        {monthlyStatuses.includes(status) && (
          <div className={cx(styles.selector, styles.selectorHalf, styles.z1)}>
            <DayOfMonthSelector
              isLoading={isLoading}
              options={selectorsData.monthly}
              onChange={dayOfMonth => {
                onSelectorChange({ monthly: dayOfMonth });
                dispatch({
                  type: PriceEvents.SELECT_DAY_OF_MONTH,
                  value: dayOfMonth,
                });
              }}
            />
          </div>
        )}

        {isIrregularMonthDay && (
          <div className={cx(styles.selector, styles.selectorFull)}>
            {renderInfoBanner()}
          </div>
        )}
      </div>
    );
  }

  if (context.frequency === 'fullPay') {
    return (
      <div className={styles.selectorContainer}>
        {renderFrequencySelector(styles.selectorHalf)}
      </div>
    );
  }

  return renderFrequencySelector();
};
